import classNames from "classnames";
import { Accordion, LoadingStepAccordionItem } from "components/Accordion";
import { RightSidebarDrawer } from "components/RightSidebar";
import { Separator } from "components/Separator";
import Shimmer from "components/Shimmer/Shimmer";
import { StatDisplay } from "components/StatDisplay";
import Tag from "components/Tag";
import Tooltip from "components/Tooltip";
import TypingAnimation from "components/TypingAnimation";
import { useAgenticSearchStrategy } from "features/Thread/hooks/useAgenticSearchStrategy";
import { Thread } from "features/Thread/types";
import { motion } from "framer-motion";
import {
  ProAgentMode,
  ProResearchGroup,
  ProResearchGroupType,
  ProResearchSearch,
  SearchMode,
} from "helpers/api";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import usePrevious from "hooks/usePrevious";
import { Eye, Info, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import SlotCounter from "react-slot-counter";
import { useSearchStepDrawer } from "./hooks/useSearchStepDrawer";
import { DeepSearchIcon, ProSearchIcon, QuickSearchIcon } from "design_system";

interface SearchStepDrawerProps {
  thread: Thread;
}

type PaperSummaryStep = {
  key: "identified" | "screened" | "eligible" | "included";
  value: number;
};

type GroupStatus = "PENDING" | "IN_PROGRESS" | "COMPLETE";

const FINAL_RANKING_ITEM_KEY = "final-ranking-item";
const READING_PAPERS_TEXT = "reading papers";

// Custom hook for managing streaming completion state
const useStreamingState = (
  interactionId: string | null,
  isCurrentInteraction: boolean
) => {
  const [streamingState, setStreamingState] = useState<
    "pending" | "showing" | "complete"
  >("pending");

  // Use localStorage hook for streaming completion state
  const storageKey = interactionId
    ? `streaming-complete-${interactionId}`
    : "dummy-key";
  const [hasCompleted, setHasCompleted] = useLocalStorageState<boolean>(
    storageKey,
    false
  );

  useEffect(() => {
    if (!interactionId) {
      setStreamingState("pending");
      return;
    }

    // For current interactions, we want to stream if not completed
    // For historical interactions, we always show as complete (no streaming)
    if (isCurrentInteraction) {
      setStreamingState(hasCompleted ? "complete" : "pending");
    } else {
      // Historical interaction - always show as complete
      setStreamingState("complete");
    }
  }, [interactionId, hasCompleted, isCurrentInteraction]);

  const markStreamingComplete = useCallback(() => {
    if (!interactionId) return;

    setHasCompleted(true);
    setStreamingState("complete");

    // Clean up old localStorage entries (keep only last 50)
    setTimeout(() => {
      const keys = Object.keys(localStorage);
      const streamingKeys = keys.filter((key) =>
        key.startsWith("streaming-complete-")
      );
      if (streamingKeys.length > 50) {
        streamingKeys.slice(0, streamingKeys.length - 50).forEach((key) => {
          localStorage.removeItem(key);
        });
      }
    }, 0);
  }, [interactionId, setHasCompleted]);

  const startShowingStreaming = useCallback(() => {
    // Only allow streaming to start for current interactions
    if (!isCurrentInteraction) return;

    setStreamingState((prevState) =>
      prevState === "pending" ? "showing" : prevState
    );
  }, [isCurrentInteraction]);

  return {
    isStreamingComplete: streamingState === "complete",
    shouldShowStreaming: streamingState === "showing",
    markStreamingComplete,
    startShowingStreaming,
  };
};

export const SearchStepDrawer = ({ thread }: SearchStepDrawerProps) => {
  const [searchStepDrawerLabels] = useLabels(
    "features.threads.search-step-drawer"
  );
  const {
    interactionId: currentInteractionId,
    isOpen,
    isActive,
    closeSearchStepDrawer,
  } = useSearchStepDrawer();

  // Use previous interaction so interaction details are still show as the drawer is closing
  const prevInteractionId = usePrevious(currentInteractionId, false);
  // Use current ID for open state, display ID for content
  const displayInteractionId = currentInteractionId ?? prevInteractionId;

  const currentInteraction = useMemo(
    () =>
      thread.interactions.find(
        (interaction) => interaction.id === displayInteractionId
      ),
    [thread.interactions, displayInteractionId]
  );

  const {
    groups,
    currentExecutingStepIndex,
    currentExecutingStepTitle,
    summaryStats,
    allGroupsComplete,
    isComplete,
  } = useAgenticSearchStrategy(displayInteractionId ?? undefined, true);

  // Simplified streaming state management
  const {
    isStreamingComplete,
    shouldShowStreaming,
    markStreamingComplete,
    startShowingStreaming,
  } = useStreamingState(
    displayInteractionId ?? null,
    displayInteractionId === currentInteractionId
  );

  // Check if first group has strategy summary and should show streaming
  const firstGroup = groups[0];
  const hasStrategySummary = Boolean(
    firstGroup?.group_order === 0 && firstGroup?.strategy_summary
  );

  // Start showing streaming when strategy summary first appears
  useEffect(() => {
    if (hasStrategySummary && !isStreamingComplete) {
      startShowingStreaming();
    }
  }, [hasStrategySummary, isStreamingComplete, startShowingStreaming]);

  const currentExecutingStepItemKey = useMemo(() => {
    // Case 1: If all groups are complete and there are more than 1 group,
    // then we need to force the final ranking item to be open
    if (allGroupsComplete && groups.length > 1) {
      return FINAL_RANKING_ITEM_KEY;
    }

    // Case 2: If the current executing step is the first non-initial search but it doesn't have any searches yet,
    // then we need to force the first search to be open since we'll be streaming with a smaller model
    if (
      currentExecutingStepIndex === 1 ||
      (currentExecutingStepIndex === 2 &&
        groups.length > 2 &&
        groups[2].searches.length === 0)
    ) {
      return "1" + groups[0].label;
    }

    // Case 3: If the first group exists and has a strategy summary but streaming is not complete,
    // then we need to force the first group to be open
    if (
      groups.length > 0 &&
      groups[0].group_order === 0 &&
      !(groups[0].strategy_summary && isStreamingComplete) &&
      !currentExecutingStepIndex
    ) {
      return "1" + groups[0].label;
    }

    return currentExecutingStepTitle && currentExecutingStepIndex
      ? currentExecutingStepIndex.toString() + currentExecutingStepTitle
      : null;
  }, [
    currentExecutingStepTitle,
    currentExecutingStepIndex,
    groups,
    allGroupsComplete,
    isStreamingComplete,
  ]);

  const paperSummarySteps: PaperSummaryStep[] = useMemo(
    () => [
      {
        key: "identified",
        value: summaryStats?.num_identified ?? 0,
      },
      {
        key: "screened",
        value: summaryStats?.num_screened ?? 0,
      },
      {
        key: "eligible",
        value: summaryStats?.num_eligible ?? 0,
      },
      {
        key: "included",
        value: summaryStats?.num_included ?? 0,
      },
    ],
    [summaryStats]
  );

  const iconAndTitle: Record<
    SearchMode,
    { icon: React.ReactNode; title: string }
  > = {
    [SearchMode.PRO_RESEARCH]: {
      icon: <DeepSearchIcon size="sm" />,
      title: searchStepDrawerLabels["deep-title"],
    },
    [SearchMode.PRO_ANALYSIS]: {
      icon: <ProSearchIcon size="sm" />,
      title: searchStepDrawerLabels["pro-title"],
    },
    [SearchMode.SUMMARY]: {
      icon: <QuickSearchIcon size="sm" />,
      title: searchStepDrawerLabels["quick-title"],
    },
  };

  const headerContent = useMemo(
    () => ({
      title: (
        <div className="flex gap-2 items-center">
          {
            iconAndTitle[
              currentInteraction?.search_mode ?? SearchMode.PRO_ANALYSIS
            ]?.icon
          }
          <span>
            {
              iconAndTitle[
                currentInteraction?.search_mode ?? SearchMode.PRO_ANALYSIS
              ]?.title
            }
          </span>
        </div>
      ),
      subtitle: currentInteraction?.user_message,
    }),
    [
      currentInteraction?.user_message,
      currentInteraction?.search_mode,
      iconAndTitle,
    ]
  );

  let accordionItems = [];
  if (groups.length === 0) {
    accordionItems = [<ThreeLoadingItems key="loading-items" />];
  } else if (
    groups.length === 1 &&
    currentInteraction?.search_mode === SearchMode.PRO_RESEARCH
  ) {
    accordionItems = [
      <LoadingStepAccordionItem
        key={"1" + groups[0].label}
        title={groups[0].label}
        content={
          <GroupContent
            group={groups[0]}
            groupStatus={
              allGroupsComplete
                ? "COMPLETE"
                : getGroupStatus(0, currentExecutingStepIndex ?? 0, groups)
            }
            onStreamingComplete={markStreamingComplete}
            hasStrategySummary={hasStrategySummary}
            shouldShowStreaming={shouldShowStreaming}
          />
        }
        itemKey={"1" + groups[0].label}
        persistentSubtitle={true}
        subtitle={
          <GroupSearchSummary
            group={groups[0]}
            searchMode={currentInteraction?.search_mode}
            proAgentMode={currentInteraction?.mode}
          />
        }
        status={"IN_PROGRESS"}
        isLast={false}
      />,
      <ThreeLoadingItems key="loading-items" />,
    ];
  } else {
    accordionItems = groups.map((group, index) => (
      <LoadingStepAccordionItem
        key={(index + 1).toString() + group.label}
        title={group.label}
        content={
          <GroupContent
            group={group}
            groupStatus={
              allGroupsComplete
                ? "COMPLETE"
                : getGroupStatus(index, currentExecutingStepIndex ?? 0, groups)
            }
            onStreamingComplete={index === 0 ? markStreamingComplete : () => {}}
            hasStrategySummary={index === 0 ? hasStrategySummary : false}
            shouldShowStreaming={index === 0 ? shouldShowStreaming : false}
          />
        }
        itemKey={(index + 1).toString() + group.label}
        persistentSubtitle={true}
        subtitle={
          <GroupSearchSummary
            group={group}
            searchMode={currentInteraction?.search_mode}
            proAgentMode={currentInteraction?.mode}
          />
        }
        status={
          allGroupsComplete
            ? "COMPLETE"
            : getGroupStatus(index, currentExecutingStepIndex ?? 0, groups)
        }
        isLast={false}
      />
    ));

    // Determine if the search is fully complete based on the search mode:
    // - PRO_RESEARCH (Deep Search): Must wait for isComplete flag since Phase 2
    //   re-ranker takes significant time to process and re-rank results
    // - PRO_ANALYSIS (Pro Agent): Can consider complete when allGroupsComplete
    //   since we use RRF (Reciprocal Rank Fusion) which executes in sub-200ms
    const isSearchComplete =
      (currentInteraction?.search_mode === SearchMode.PRO_RESEARCH &&
        isComplete) ||
      (currentInteraction?.search_mode === SearchMode.PRO_ANALYSIS &&
        allGroupsComplete);

    // Map the completion state to status for the final ranking UI:
    // - COMPLETE: Search is fully done (re-ranking/RRF finished)
    // - IN_PROGRESS: All search groups done but final ranking still processing
    //   (only applies to PRO_RESEARCH waiting for re-ranker)
    // - PENDING: Still executing search groups
    const searchStatus = isSearchComplete
      ? "COMPLETE"
      : allGroupsComplete
        ? "IN_PROGRESS"
        : "PENDING";

    // Add final ranking item
    accordionItems.push(
      <LoadingStepAccordionItem
        key={FINAL_RANKING_ITEM_KEY}
        title={
          currentInteraction?.mode === ProAgentMode.ANALYSIS_FOLLOW_UP
            ? searchStepDrawerLabels["generating-analysis"]
            : searchStepDrawerLabels["final-ranking-title"]
        }
        content={
          <p className="sm-normal text-fg-base">
            {searchStepDrawerLabels["final-ranking-description"].replace(
              "{PAPER_COUNT}",
              summaryStats?.num_eligible ?? 0
            )}
          </p>
        }
        itemKey={FINAL_RANKING_ITEM_KEY}
        status={searchStatus}
        isLast
      />
    );
  }

  const isDeepSearch =
    currentInteraction?.search_mode === SearchMode.PRO_RESEARCH;

  return (
    <RightSidebarDrawer
      isOpen={isOpen}
      isActive={isActive}
      onClose={closeSearchStepDrawer}
      fullHeight
      hideScrollBar
      headerContent={headerContent}
      size="lg"
    >
      <div className="flex flex-col gap-4 p-2">
        {/* Total papers summary only shown for Deep Search */}
        {isDeepSearch && (
          <div className="flex gap-6 items-center mt-2 mx-2 pb-4 border-b border-border-base">
            {paperSummarySteps.map((step) => (
              <PaperSummaryStep
                key={step.key}
                step={step}
                isComplete={isComplete}
              />
            ))}
          </div>
        )}
        {/* Steps */}
        <div>
          <Accordion forceOpenItem={currentExecutingStepItemKey}>
            {accordionItems}
          </Accordion>
        </div>
      </div>
    </RightSidebarDrawer>
  );
};

const PaperSummaryStep = ({
  step,
  isComplete,
}: {
  step: PaperSummaryStep;
  isComplete?: boolean;
}) => {
  const [searchStepDrawerLabels] = useLabels(
    "features.threads.search-step-drawer"
  );

  const stepValue = useMemo(() => {
    if (step.key === "included" && !isComplete) {
      return "—";
    }
    return step.value ? <SlotCounter value={step.value} /> : "—";
  }, [step.key, step.value, isComplete]);

  return (
    <>
      <div className="flex flex-col">
        <div className="flex gap-2 items-center">
          <span className="lg-mono-bold text-fg-base">{stepValue}</span>
          <Tooltip
            content={
              <span className="max-w-[260px] text-center">
                {searchStepDrawerLabels[step.key + "-tooltip"]}
              </span>
            }
            placement="top"
          >
            <Info size={12} className="text-fg-muted" />
          </Tooltip>
        </div>
        <span className="tiny-normal text-fg-muted">
          {searchStepDrawerLabels[step.key]}
        </span>
      </div>
      <div className="w-px h-4 bg-bg-subtle last:hidden" />
    </>
  );
};

const getGroupStatus = (
  groupIndex: number,
  currentExecutingStepIndex: number,
  groups: ProResearchGroup[]
): GroupStatus => {
  const currentZeroBasedExecutingStepIndex = currentExecutingStepIndex - 1;

  // If setting the first group, ensure that the second group has moved to 'in progress' before the first group
  // is marked as 'complete'
  if (
    groupIndex === 0 &&
    currentZeroBasedExecutingStepIndex === 1 &&
    groups[1].searches.length == 0
  ) {
    return "IN_PROGRESS";
  }

  // If current index is the same as the executing step index and the group at the index has searches
  // then the group is in progress
  if (
    groupIndex === currentZeroBasedExecutingStepIndex &&
    groups[groupIndex].searches.length > 0
  ) {
    return "IN_PROGRESS";
  }

  // If the current index is less than the executing step index, then the group is complete
  if (groupIndex < currentZeroBasedExecutingStepIndex) {
    return "COMPLETE";
  }
  return "PENDING";
};

const GroupSearchSummary = ({
  group,
  searchMode,
  proAgentMode,
}: {
  group: ProResearchGroup;
  searchMode: SearchMode | undefined;
  proAgentMode: ProAgentMode | undefined;
}) => {
  const [searchStepDrawerLabels] = useLabels(
    "features.threads.search-step-drawer"
  );

  const { totalSearches, totalResults, groupSearchesComplete } = useMemo(() => {
    if (group.searches.length === 0) {
      return {
        totalSearches: 0,
        totalResults: 0,
        groupSearchesComplete: false,
      };
    }

    const totalSearches = group.searches.length;
    const totalResults = group.searches.reduce(
      (acc, search) => acc + (search.result_count ?? 0),
      0
    );
    const groupSearchesComplete =
      totalSearches > 0 &&
      group.searches.every((search) => search.status === "COMPLETE");

    return { totalSearches, totalResults, groupSearchesComplete };
  }, [group.searches]);

  // For citation graph groups, hide the search count but keep filters and results
  const isCitationGraph =
    group.group_type === ProResearchGroupType.CITATION_GRAPH;

  if (!isCitationGraph && totalSearches === 0) {
    return null;
  }

  const filtersCount = group.applied_filters?.length || 0;
  const showFilters = filtersCount > 0;

  const isProSearch = searchMode === SearchMode.PRO_ANALYSIS;

  if (proAgentMode === ProAgentMode.ANALYSIS_FOLLOW_UP) {
    return null;
  }

  return (
    <div className="flex gap-2 items-baseline">
      {/* Hide search count for citation graph groups */}
      {!isCitationGraph && (
        <>
          <StatDisplay
            count={groupSearchesComplete ? totalSearches : totalSearches || "—"}
            label={pluralize(searchStepDrawerLabels["searches"], {
              SEARCH_COUNT: totalSearches,
            })}
            slotCounter={totalSearches !== 0}
          />
          {!isProSearch && !showFilters && <Separator />}
        </>
      )}
      {showFilters && (
        <>
          <StatDisplay
            count={filtersCount}
            label={pluralize(searchStepDrawerLabels["filters"], {
              FILTER_COUNT: filtersCount,
            })}
            slotCounter={filtersCount !== 0}
          />
          {!isProSearch && <Separator />}
        </>
      )}
      {/* Show results count for both regular and citation graph groups */}
      {!isProSearch && (
        <StatDisplay
          count={groupSearchesComplete ? totalResults : totalResults || "—"}
          label={pluralize(searchStepDrawerLabels["eligible-papers"], {
            PAPER_COUNT: totalResults,
          })}
          slotCounter={totalResults !== 0}
        />
      )}
    </div>
  );
};

const getSearchMotionProps = ({
  status,
  delay,
}: {
  status: ProResearchSearch["status"];
  delay: number;
}) => {
  return status !== "COMPLETE"
    ? {
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 },
        transition: {
          duration: 0.3,
          ease: "easeOut",
          delay,
        },
      }
    : {};
};

const GroupContent = ({
  group,
  groupStatus,
  onStreamingComplete,
  hasStrategySummary,
  shouldShowStreaming,
}: {
  group: ProResearchGroup;
  groupStatus: GroupStatus;
  onStreamingComplete: () => void;
  hasStrategySummary: boolean;
  shouldShowStreaming: boolean;
}) => {
  return (
    <div className="flex flex-col gap-6">
      {/* Hide searches list for citation graph groups */}
      {group.group_type !== ProResearchGroupType.CITATION_GRAPH &&
        group.searches.length > 0 && (
          <div className="flex flex-col gap-3">
            {group.searches.map((search, index) => {
              const motionProps = getSearchMotionProps({
                status: search.status,
                delay: index * 0.1,
              });

              return (
                <motion.div
                  key={`${index}-${search.search_id}`}
                  {...motionProps}
                  className={classNames("flex gap-3 items-center")}
                >
                  {search.query.toLowerCase() === READING_PAPERS_TEXT ? (
                    <Eye size={16} className="flex-none text-fg-muted" />
                  ) : (
                    <Search size={16} className="flex-none text-fg-muted" />
                  )}
                  <Shimmer
                    isActive={groupStatus !== "COMPLETE"}
                    className="sm-normal text-fg-muted leading-[normal]"
                  >
                    {search.query}
                  </Shimmer>
                </motion.div>
              );
            })}
          </div>
        )}
      {((group.applied_filters && group.applied_filters.length > 0) ||
        group.description) && (
        <div className="flex flex-col gap-4">
          {group.applied_filters && group.applied_filters.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {group.applied_filters.map((filter, index) => (
                <Tag
                  label={filter}
                  icon="icon-list-filter"
                  key={index}
                  size="sm"
                />
              ))}
            </div>
          )}
          {group.description && (
            <span className="sm-normal text-fg-base">
              {group.description}
              {group.group_order === 0 && hasStrategySummary && (
                <div className="mt-6">
                  {shouldShowStreaming ? (
                    <TypingAnimation
                      text={group.strategy_summary!}
                      className="sm-normal text-fg-base"
                      onComplete={onStreamingComplete}
                      isActive={shouldShowStreaming}
                      speed={3}
                    />
                  ) : (
                    <span className="sm-normal text-fg-base">
                      {group.strategy_summary}
                    </span>
                  )}
                </div>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ThreeLoadingItems = () => {
  const loadingItemWidths = ["w-[281px]", "w-[363px]", "w-[165px]"];
  return (
    <>
      {Array.from({ length: 3 }).map((_, idx) => (
        <LoadingStepAccordionItem
          key={`loader-${idx}`}
          title={
            <div
              className={`mx-auto h-4 rounded-full animate-pulse ${loadingItemWidths[idx]} bg-border-base`}
            />
          }
          itemKey={`loader-${idx}`}
          status="PENDING"
          isLast={idx === 2}
          content={null}
          hideChevron={true}
        />
      ))}
    </>
  );
};
